import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon, ArrowDownToLineIcon, ExternalLinkIcon } from 'lucide-react';
import { Book } from '../types';
import { projectsByBook } from '../data/projects';
import { toDirect, toView } from '../data/links';
interface BookDrawerProps {
  book: Book | null;
  onClose: () => void;
}
const pad = (value: number) => String(value).padStart(2, '0');
export function BookDrawer({
  book,
  onClose
}: BookDrawerProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!book) return undefined;
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [book, onClose]);
  const view = book ? toView(book.file) : '';
  const dl = book ? toDirect(book.file) : '';
  return <AnimatePresence data-id="element-75" data-mp="fflkc0oyahl1">
      {book ? <div className="fixed inset-0 z-50 flex justify-end" data-id="element-76" data-mp="19kh2v9yfz2n1">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={onClose} className="absolute inset-0 bg-ink/35" data-id="element-77" data-mp="19n39n7dide47" />
          <motion.aside role="dialog" aria-modal="true" aria-label={`Projects in Book ${pad(book.number)}, ${book.title}`} initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        duration: 0.28,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative flex h-full w-full max-w-[27rem] flex-col border-l border-rule bg-surface" data-id="element-78" data-mp="14o01vf658tsj">
            <div className="h-[3px] w-full shrink-0" style={{
          backgroundColor: book.accent
        }} data-id="element-79" data-mp="2ba2mb76y75b9" />
            <header className="flex items-start justify-between gap-4 border-b border-rule px-6 py-5" data-id="element-80" data-mp="2g1kpm1wg1hze">
              <div data-id="element-81" data-mp="2pxb26lb7bytk">
                <p className="tnum font-sans text-[11px] tracking-[0.16em]" style={{
              color: book.accent
            }} data-id="element-82" data-mp="2pvq9l18g6pyc">
                  BOOK {pad(book.number)}
                </p>
                <h3 className="mt-2 font-display font-semibold text-[24px] leading-tight text-ink" data-id="element-83" data-mp="2fyc23r4i5bi6">{book.title}</h3>
                <p className="mt-1 font-sans text-[14px] text-ink/60" data-id="element-84" data-mp="2bw8ey58h0ih2">
                  {book.projectCount} projects · {book.pages} pages
                </p>
              </div>
              <button ref={closeRef} type="button" onClick={onClose} aria-label="Close project list" className="border border-rule p-2 text-ink/70 transition-colors duration-150 ease-out hover:border-ink/45 hover:text-ink focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ink" data-id="element-85" data-mp="226ymgo9psxsf">
                <XIcon aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} data-id="element-86" data-mp="1j8upstb0kr7i" />
              </button>
            </header>

            {view ? <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-rule px-6 py-4" data-id="element-87" data-mp="3uvg2ggr0hte7">
                <a href={view} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-green px-3.5 py-2.5 font-sans text-[13px] leading-none text-onaccent transition-colors duration-150 ease-out hover:bg-[#33684D] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ink" data-id="element-88" data-mp="3nttc8liwypy">
                  <ExternalLinkIcon aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} data-id="element-89" data-mp="3g7bizak4xdud" />
                  Open the whole book
                </a>
                <a href={dl} className="inline-flex items-center gap-1.5 font-sans text-[12.5px] text-muted underline decoration-rule underline-offset-4 transition-colors duration-150 ease-out hover:text-ink hover:decoration-ink focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ink" data-id="element-90" data-mp="3pxemw5ssoc7h">
                  <ArrowDownToLineIcon aria-hidden="true" className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-91" data-mp="13vqtgcxjsezy" />
                  Download
                </a>
              </div> : null}

            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-2" data-id="element-92" data-mp="1crg8m48ekd4w">
              <ul className="divide-y divide-rule/70" data-id="element-93" data-mp="17ydoempimg1d">
                {projectsByBook(book.number).map(project => <li key={project.id} className="flex gap-4 py-3" data-id="element-94" data-mp="1mmrst22kyck1">
                    <span className="tnum w-6 shrink-0 pt-1 font-sans text-[12px] text-ink/35" data-id="element-95" data-mp="1hq1dwi2ihn07">
                      {pad(project.projectNumber)}
                    </span>
                    <div className="min-w-0" data-id="element-96" data-mp="1rnp6p2gk4kfq">
                      <p className="font-display text-[16px] leading-snug text-ink" data-id="element-97" data-mp="1rnxkcwki7trz">{project.title}</p>
                      <p className="font-sans text-[14px] leading-snug text-ink/50" data-id="element-98" data-mp="1bi50fvzujb2">
                        {project.subtitle}
                      </p>
                      <p className="tnum mt-1 font-sans text-[11px] text-ink/45" data-id="element-99" data-mp="3jac3f3wj2rz4">
                        Page {project.page} · Ages {project.age} · {project.time} · {project.difficulty}
                      </p>
                    </div>
                  </li>)}
              </ul>
            </div>
          </motion.aside>
        </div> : null}
    </AnimatePresence>;
}